/**
 * Public Pricing Page (Infirmly)
 * Two-card layout: Client (CHF 19/year) + Expert (CHF 179/year)
 * Brand color: #0044CC
 */
import { useNavigate } from "react-router-dom";
import { useAuth, useLanguage } from "@/context/AppContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, ShieldCheck } from "lucide-react";

const PricingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();

  const startFlow = (role) => {
    if (user) {
      navigate(`/dashboard/add-role?role=${role}`);
    } else {
      navigate(`/register?role=${role}`);
    }
  };

  const clientFeatures = [
    t("pricing_client_f1") || "Unlimited requests",
    t("pricing_client_f2") || "Direct chat with experts",
    t("pricing_client_f3") || "Appointment booking",
    t("pricing_client_f4") || "Verified professionals only",
  ];
  const expertFeatures = [
    t("pricing_expert_f1") || "Receive client requests",
    t("pricing_expert_f2") || "Verified badge",
    t("pricing_expert_f3") || "Appointment management",
    t("pricing_expert_f4") || "Priority listing",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A1838] via-[#0A1838] to-[#0A1838] text-white" data-testid="pricing-page">
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-24">
        <div className="text-center mb-10 sm:mb-12">
          <h1 className="text-3xl sm:text-5xl font-extrabold mb-3 text-balance">{t("pricingPlans") || "Pricing Plans"}</h1>
          <p className="text-blue-100/90 text-base sm:text-lg">
            {t("pricingSubtitle") || "Choose your annual plan — No hidden costs"}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {/* Client */}
          <Card className="bg-white text-gray-900 rounded-2xl shadow-xl border-0 overflow-hidden" data-testid="pricing-card-client">
            <CardContent className="p-8">
              <div className="mb-1 text-sm font-semibold text-[#0044CC] uppercase tracking-wide">
                {t("client") || "Client"}
              </div>
              <h2 className="text-2xl font-bold mb-1">{t("forClientsSubtitle") || "For those seeking expert advice"}</h2>
              <div className="my-6 flex items-baseline gap-2">
                <span className="text-5xl font-extrabold text-gray-900">CHF 19</span>
                <span className="text-gray-500">/{t("year") || "year"}</span>
              </div>
              <p className="text-xs uppercase tracking-wider text-gray-400 mb-4">{t("billedYearly") || "Billed yearly"}</p>
              <ul className="space-y-2.5 mb-8">
                {clientFeatures.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-gray-700">
                    <Check className="w-4 h-4 text-[#0044CC] flex-shrink-0" />
                    <span className="text-sm">{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => startFlow("client")}
                className="w-full bg-[#0044CC] hover:bg-[#0033AA] text-white h-12 rounded-full text-base font-semibold"
                data-testid="pricing-client-cta"
              >
                {t("startNow") || "Start Now"}
              </Button>
            </CardContent>
          </Card>

          {/* Expert (highlighted) */}
          <Card className="bg-gradient-to-br from-[#0044CC] to-[#0033AA] text-white rounded-2xl shadow-2xl border-0 overflow-hidden relative ring-1 ring-white/20" data-testid="pricing-card-expert">
            <Badge className="absolute top-4 right-4 bg-amber-400 text-gray-900 font-bold tracking-wider" data-testid="pricing-expert-pro-badge">
              PRO
            </Badge>
            <CardContent className="p-8">
              <div className="mb-1 text-sm font-semibold text-amber-300 uppercase tracking-wide">
                {t("expert") || "Expert"}
              </div>
              <h2 className="text-2xl font-bold mb-1">{t("forExpertsSubtitle") || "For professionals offering services"}</h2>
              <div className="my-6 flex items-baseline gap-2">
                <span className="text-5xl font-extrabold">CHF 179</span>
                <span className="text-white/70">/{t("year") || "year"}</span>
              </div>
              <p className="text-xs uppercase tracking-wider text-white/60 mb-4">{t("billedYearly") || "Billed yearly"}</p>
              <ul className="space-y-2.5 mb-8">
                {expertFeatures.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-white/90">
                    <Check className="w-4 h-4 text-amber-300 flex-shrink-0" />
                    <span className="text-sm">{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => startFlow("provider")}
                className="w-full bg-white text-[#0044CC] hover:bg-gray-100 h-12 rounded-full text-base font-semibold"
                data-testid="pricing-expert-cta"
              >
                {t("startNow") || "Start Now"}
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="mt-10 sm:mt-12 flex items-center justify-center gap-2 text-sm text-blue-100/80">
          <ShieldCheck className="w-4 h-4" aria-hidden="true" />
          <span>{t("paymentViaWorldline") || "Payment via Worldline · 100% Swiss"}</span>
        </div>
      </main>
    </div>
  );
};

export default PricingPage;
